import { PlayCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Card, Empty, Input, Space, Tag, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "../../components/PageHeader";
import { findModule } from "../../helpers/nav";
import { useTutorial } from "../../tutorials/TutorialContext";
import type { Tutorial } from "../../tutorials/types";

/**
 * How well a guide matches what was typed: every word of the query has to be found in the
 * guide's title, description or module - as a substring, or failing that as a subsequence
 * of some word (so "recpt" still finds "receipt"). Substring hits rank above subsequence
 * ones, and a title hit above a description hit, so the obvious guide comes first.
 */
function fuzzyScore(query: string, t: Tutorial, moduleLabel: string): number {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 1;
  const fields = [t.title, moduleLabel, t.description].map((f) =>
    f.toLowerCase(),
  );
  let score = 0;
  for (const w of words) {
    let best = 0;
    fields.forEach((field, i) => {
      const weight = 3 - i; // title 3, module 2, description 1
      if (field.includes(w)) best = Math.max(best, 10 * weight);
      else if (
        field.split(/[^a-z0-9]+/).some((token) => isSubsequence(w, token))
      )
        best = Math.max(best, weight);
    });
    if (best === 0) return 0;
    score += best;
  }
  return score;
}

function isSubsequence(needle: string, hay: string): boolean {
  let i = 0;
  for (const ch of hay) if (ch === needle[i]) i++;
  return i === needle.length;
}

/**
 * Every guided walkthrough, grouped the way the home page groups modules and searchable.
 * Starting one swaps the whole app onto sample data until it is exited
 * (tutorials/engine/session.ts).
 */
export default function TutorialsPage() {
  const { start } = useTutorial();
  // The guides are a lazy chunk (with their sample data behind them): loaded here, on the
  // one page that lists them, rather than carried by every page of the app.
  const [tutorials, setTutorials] = useState<Tutorial[]>([]);
  const [query, setQuery] = useState("");
  useEffect(() => {
    import("../../tutorials/registry").then((m) => setTutorials(m.TUTORIALS));
  }, []);

  const groups = useMemo(() => {
    const scored = tutorials
      .map((t) => ({
        t,
        score: fuzzyScore(
          query,
          t,
          findModule(t.moduleKey)?.label ?? t.moduleKey,
        ),
      }))
      .filter((x) => x.score > 0);
    // Registry order (the core loop first) when browsing; best match first when searching.
    if (query.trim()) scored.sort((a, b) => b.score - a.score);
    const out = new Map<string, Tutorial[]>();
    for (const { t } of scored)
      out.set(t.moduleKey, [...(out.get(t.moduleKey) ?? []), t]);
    return out;
  }, [tutorials, query]);

  return (
    <div>
      <PageHeader
        title="Tutorials"
        subtitle="Step-by-step walkthroughs of how each part of Glut works."
        extra={
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search tutorials..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ width: 280 }}
            data-tour="tutorials-search"
          />
        }
      />
      {tutorials.length > 0 && groups.size === 0 && (
        <Empty description={`No tutorial matches "${query.trim()}".`} />
      )}
      {[...groups.entries()].map(([moduleKey, list]) => (
        <section key={moduleKey} style={{ marginBottom: 24 }}>
          <Typography.Title level={5} style={{ marginBottom: 12 }}>
            {findModule(moduleKey)?.label ?? moduleKey}
          </Typography.Title>
          <div className="glut-card-grid">
            {list.map((t) => (
              <Card
                key={t.id}
                className="glut-tutorial-card"
                data-tour={`tutorial-${t.id}`}
              >
                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                  <Typography.Text strong>{t.title}</Typography.Text>
                  <Typography.Text type="secondary">
                    {t.description}
                  </Typography.Text>
                  <Space size={4} wrap>
                    <Tag>{t.minutes} min</Tag>
                    <Tag>{t.steps.length} steps</Tag>
                  </Space>
                  <Button
                    type="primary"
                    icon={<PlayCircleOutlined />}
                    onClick={() => start(t.id)}
                  >
                    Start
                  </Button>
                </Space>
              </Card>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
